import { useEffect, useState } from 'react';

/** A lead card whose next action Sales is rewriting from a voice call. */
const working = new Map<number, number>();
let liveCallId: number | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

export function markSalesCardWorking(opportunityId: number) {
  if (!Number.isFinite(opportunityId) || opportunityId <= 0) return;
  working.set(opportunityId, Date.now());
  emit();
}

export function settleSalesCard(opportunityId?: number) {
  if (opportunityId == null) working.clear();
  else working.delete(opportunityId);
  emit();
}

/** Leave the call and show “updating” in one paint, so the draft does not flash through. */
export function showSalesCardUpdating(opportunityId: number) {
  if (!Number.isFinite(opportunityId) || opportunityId <= 0) return;
  working.set(opportunityId, Date.now());
  if (liveCallId === opportunityId) liveCallId = null;
  emit();
}

/** The lead whose card is currently the voice surface. */
export function setSalesCardCall(opportunityId: number | null) {
  const next = opportunityId != null && opportunityId > 0 ? opportunityId : null;
  if (next === liveCallId) return;
  liveCallId = next;
  emit();
}

export function useSalesCardCall(opportunityId: number): boolean {
  const [on, setOn] = useState(false);

  useEffect(() => {
    const sync = () => setOn(liveCallId === opportunityId);
    listeners.add(sync);
    sync();
    return () => { listeners.delete(sync); };
  }, [opportunityId]);

  return on;
}

/** True until a replacement play lands, the turn ends, or two minutes pass. */
export function useSalesCardWorking(opportunityId: number, playCreatedAt: string): boolean {
  const [on, setOn] = useState(false);

  useEffect(() => {
    const sync = () => {
      const started = working.get(opportunityId);
      if (!started) {
        setOn(false);
        return;
      }
      const created = Date.parse(playCreatedAt);
      setOn(!(Number.isFinite(created) && created >= started - 15_000));
    };
    listeners.add(sync);
    sync();
    return () => { listeners.delete(sync); };
  }, [opportunityId, playCreatedAt]);

  useEffect(() => {
    if (!on) return;
    const timer = window.setTimeout(() => settleSalesCard(opportunityId), 120_000);
    return () => window.clearTimeout(timer);
  }, [on, opportunityId]);

  return on;
}
